// ✅ ALTERED ONLY FOR THEME (Orange header bar + Blue surfaces like your "above theme")
// - Orange gradient header
// - Page bg #F2F2F2, inner white cards with subtle blue border
// - Inputs: soft blue border + orange focus
// - Buttons: primary orange, outlined blue, warning/disable stays noticeable
// - Telegram + Announcements sections use accent stripe + consistent spacing
// - No business logic changed

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { startRegistration, browserSupportsWebAuthn } from "@simplewebauthn/browser";
import {
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import { API_URL } from "./config";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // 'success' | 'warning'
  const [error, setError] = useState("");
  const [logincampusCode, setLoginCampusCode] = useState("");

  // 2FA setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [loading2FA, setLoading2FA] = useState(false);

  // Passkey registration state
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyName, setPasskeyName] = useState("");
  const [passkeySupported, setPasskeySupported] = useState(false);

  // ✅ Telegram settings state
  const [tgLoading, setTgLoading] = useState(false);
  const [tgStatus, setTgStatus] = useState(null); // { linked, enabled, linkedAt, lastError... }
  const [tgLink, setTgLink] = useState("");
  const [tgAnnounce, setTgAnnounce] = useState(null); // { enabled, inviteLink }

  const navigate = useNavigate();
  const token = localStorage.getItem("auth");

  // ---------------- THEME (matches your portal theme) ----------------
  const ORANGE = "#F07828";
  const ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  const rootPaperSx = {
    maxWidth: 520,
    mx: "auto",
    my: 2,
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${ORANGE} 0%, ${ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const cardSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
    p: 2,
  };

  const sectionSx = {
    ...cardSx,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${ORANGE} 0%, ${ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: ORANGE,
    "&:hover": { backgroundColor: ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  // Helper: decode token for username + campusCode (so 2FA setup can work)
  const getDecoded = () => {
    if (!token) return null;
    try {
      return JSON.parse(atob(token.split(".")[1]));
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const decoded = getDecoded();
    if (decoded?.campusCode) setLoginCampusCode(decoded.campusCode);
  }, [token]);

  useEffect(() => {
    try {
      setPasskeySupported(browserSupportsWebAuthn());
    } catch {
      setPasskeySupported(false);
    }
  }, []);

  const base = (API_URL || "").replace(/\/+$/, "");
  const postAuth = (path, body) =>
    axios.post(`${base}/api/auth/${path}`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });

  // ✅ Telegram API helpers
  const tgHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const loadTelegram = async () => {
    if (!token) return;

    setTgLoading(true);
    setError("");

    try {
      const [statusRes, linkRes, announceRes] = await Promise.allSettled([
        axios.get(`${base}/api/telegram/me/status`, { headers: tgHeaders }),
        axios.get(`${base}/api/telegram/me/link`, { headers: tgHeaders }),
        axios.get(`${base}/api/telegram/me/announcements`, { headers: tgHeaders }),
      ]);

      if (statusRes.status === "fulfilled") {
        setTgStatus(statusRes.value.data);
      }

      if (linkRes.status === "fulfilled") {
        setTgLink(linkRes.value.data?.url || "");
      } else {
        console.warn("Telegram link failed:", linkRes.reason?.response?.data || linkRes.reason?.message);
        setTgLink("");
      }

      if (announceRes.status === "fulfilled") {
        setTgAnnounce(announceRes.value.data);
      } else {
        console.warn("Telegram announcements failed:", announceRes.reason?.response?.data || announceRes.reason?.message);
        setTgAnnounce(null);
      }
    } finally {
      setTgLoading(false);
    }
  };

  const disableTelegram = async () => {
    if (!token) return;
    setTgLoading(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${base}/api/telegram/me/disable`, {}, { headers: tgHeaders });
      await loadTelegram();
      setMessageType("success");
      setMessage("Telegram alerts disabled.");
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to disable Telegram alerts");
    } finally {
      setTgLoading(false);
    }
  };

  const openTelegramLink = () => {
    if (!tgLink) {
      setError("Telegram link not available. Please try again.");
      return;
    }
    window.open(tgLink, "_blank", "noopener,noreferrer");
  };

  // Load telegram status on page load
  useEffect(() => {
    loadTelegram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const renderAnnouncementsSection = () => {
    const invite = tgAnnounce?.notificationsInviteLink || tgAnnounce?.inviteLink || "";
    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 900, color: ORANGE }}>Campus Announcements</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Join your campus announcements channel to receive broadcast updates (feedback forms, exams,
          holidays).
        </Typography>

        {!invite ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            Announcements channel is not configured for your campus yet.
          </Alert>
        ) : (
          <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => window.open(invite, "_blank", "noopener,noreferrer")}
              sx={primaryBtnSx}
              type="button"
            >
              Join Announcements
            </Button>
          </Stack>
        )}
      </Box>
    );
  };

  const handleResetPassword = async () => {
    setError("");
    setMessage("");

    if (newPassword.length < 8) {
      setError("Error: Password must be at least 8 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!token) {
      setError("You must be logged in to reset your password");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${base}/api/auth/reset-password`,
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const serverMsg = data?.message || "Password updated successfully";
      setMessageType("success");
      setMessage(serverMsg);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const serverErr =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (typeof err.response?.data === "string" ? err.response.data : "") ||
        "Unknown error occurred";

      setError(serverErr);
      setMessage("");
    } finally {
      setLoading(false);
    }
  };

  // Step 1: Create/Update secret and get QR
  const start2FARegistration = async () => {
    setError("");
    setMessage("");

    const decoded = getDecoded();
    const username = decoded?.username;
    const campusCode = decoded?.campusCode;

    if (!username || !campusCode) {
      setError("Cannot start 2FA setup: username/campusCode missing in token.");
      return;
    }

    setLoading2FA(true);
    try {
      const res = await postAuth("register-authenticator", { username, campusCode });
      setQrCode(res.data.qrCode);
      setShow2FASetup(true);
      setMessageType("warning");
      setMessage("Please set up your Authenticator (2FA). Don't refresh the page");
    } catch (err) {
      const msg = err.response?.data?.message || "Unable to start 2FA setup.";

      if (/already|exists|enabled|secret/i.test(msg)) {
        setShow2FASetup(false);
        setQrCode("");
        setMessage("Password updated. 2FA is already enabled for your account.");
        return;
      }

      setError(msg);
    } finally {
      setLoading2FA(false);
    }
  };

  // Step 2: Verify the first code
  const handleVerify2FA = async () => {
    setError("");
    setMessage("");

    const decoded = getDecoded();
    const username = decoded?.username;
    const campusCode = decoded?.campusCode;

    if (!username || !campusCode) {
      setError("Cannot verify 2FA: username/campusCode missing in token.");
      return;
    }

    if (!authToken || authToken.trim().length < 6) {
      setError("Enter the 6-digit code from your Authenticator app.");
      return;
    }

    setLoading2FA(true);
    try {
      await postAuth("verify-register-authenticator", {
        username,
        campusCode,
        token: authToken.trim(),
      });

      setMessageType("success");
      setMessage("✅ 2FA enabled successfully!");
      setError("");
      setAuthToken("");
      setQrCode("");
      setShow2FASetup(false);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid authentication code. Try again.");
    } finally {
      setLoading2FA(false);
    }
  };

  // Register a passkey for the currently authenticated user
  const handleRegisterPasskey = async () => {
    setError("");
    setMessage("");

    if (!token) {
      setError("You must be logged in to register a passkey.");
      return;
    }

    if (!passkeySupported) {
      setError("Passkeys are not supported by this browser or device.");
      return;
    }

    setPasskeyLoading(true);

    try {
      // 1. Ask UniFLOO backend for WebAuthn registration options.
      const optionsRes = await axios.post(
        `${base}/api/passkeys/register/options`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { challengeId, options } = optionsRes.data || {};

      if (!challengeId || !options) {
        throw new Error("Invalid passkey registration response from server.");
      }

      // 2. Browser/OS performs Face ID, Touch ID, Windows Hello, etc.
      const registrationResponse = await startRegistration({
        optionsJSON: options,
      });

      // 3. Verify and persist the credential on the backend.
      const verifyRes = await axios.post(
        `${base}/api/passkeys/register/verify`,
        {
          challengeId,
          response: registrationResponse,
          friendlyName:
            passkeyName.trim() ||
            `${navigator.platform || "Device"} passkey`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!verifyRes.data?.verified) {
        throw new Error("Passkey verification failed.");
      }

      setPasskeyName("");
      setMessageType("success");
      setMessage(
        "Passkey registered successfully. You can now use this device for passwordless sign-in."
      );
    } catch (err) {
      // NotAllowedError is commonly returned when the user cancels the OS prompt.
      if (err?.name === "NotAllowedError") {
        setError("Passkey registration was cancelled or timed out.");
      } else if (err?.name === "InvalidStateError") {
        setError("This authenticator appears to already be registered for your account.");
      } else {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to register passkey."
        );
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  // Helper to render Telegram section
  const renderTelegramSection = () => {
    const linked = tgStatus?.linked === true;
    const enabled = tgStatus?.enabled === true;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 900, color: ORANGE }}>Telegram Alerts</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Enable Telegram to receive important alerts.
        </Typography>

        {tgStatus?.lastError ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Last issue: {tgStatus.lastError}
          </Alert>
        ) : null}

        {!token ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Please login to manage Telegram alerts.
          </Alert>
        ) : (
          <>
            {linked ? (
              <Alert severity={enabled ? "success" : "warning"} sx={{ mt: 1 }}>
                {enabled
                  ? "✅ Telegram alerts are enabled."
                  : "⚠️ Telegram is linked but alerts are disabled."}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                Telegram is not linked yet. Click Enable, then press <b>Start</b> in Telegram.
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="center">
              <Button
                variant={linked ? "outlined" : "contained"}
                onClick={openTelegramLink}
                disabled={tgLoading || !tgLink}
                sx={linked ? outlinedBtnSx : primaryBtnSx}
                type="button"
              >
                {tgLoading ? (
                  <CircularProgress size={20} />
                ) : linked ? (
                  "Open / Re-link"
                ) : (
                  "Enable Telegram"
                )}
              </Button>

              {linked && enabled && (
                <Button
                  variant="contained"
                  onClick={disableTelegram}
                  disabled={tgLoading}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: "rgba(244,67,54,0.90)",
                    "&:hover": { backgroundColor: "rgba(244,67,54,1)" },
                  }}
                  type="button"
                >
                  {tgLoading ? <CircularProgress size={20} /> : "Disable"}
                </Button>
              )}

              {linked && !enabled && (
                <Button
                  variant="contained"
                  onClick={async () => {
                    setTgLoading(true);
                    setError("");
                    setMessage("");
                    try {
                      await axios.post(`${base}/api/telegram/me/enable`, {}, { headers: tgHeaders });
                      await loadTelegram();
                      setMessageType("success");
                      setMessage("Telegram alerts enabled.");
                    } catch (e) {
                      setError(e?.response?.data?.message || "Failed to enable Telegram alerts");
                    } finally {
                      setTgLoading(false);
                    }
                  }}
                  disabled={tgLoading}
                  sx={primaryBtnSx}
                  type="button"
                >
                  Enable Alerts
                </Button>
              )}

              <Button variant="text" onClick={loadTelegram} disabled={tgLoading} type="button">
                Refresh
              </Button>
            </Stack>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ backgroundColor: BG, p: 2 }}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Security Settings
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Manage your password, passkeys, two-factor authentication, push notifications, and alerts
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {/* Alerts */}
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {message ? (
            <Alert severity={messageType} sx={{ mb: 2 }}>
              {message}
            </Alert>
          ) : null}

          {/* Main card */}
          <Paper variant="outlined" sx={cardSx}>
            {!show2FASetup ? (
              <>
                <Stack spacing={2}>
                  <Paper variant="outlined" sx={sectionSx}>
                    <Box sx={{ pl: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: ORANGE }}>
                        Password
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                        Choose a strong password that you do not use on other services.
                      </Typography>

                      <TextField
                        label="New Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={inputSx}
                      />

                      <TextField
                        label="Confirm Password"
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={!!confirmPassword && confirmPassword !== newPassword}
                        helperText={
                          !!confirmPassword && confirmPassword !== newPassword
                            ? "Passwords do not match"
                            : ""
                        }
                        sx={inputSx}
                      />

                      <Button
                        variant="contained"
                        onClick={handleResetPassword}
                        fullWidth
                        sx={{ ...primaryBtnSx, mt: 1 }}
                        disabled={
                          loading ||
                          !newPassword.trim() ||
                          !confirmPassword.trim() ||
                          newPassword !== confirmPassword
                        }
                        type="button"
                      >
                        {loading ? <CircularProgress size={20} /> : "Update Password"}
                      </Button>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={sectionSx}>
                    <Box sx={{ pl: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: ORANGE }}>
                        Passkeys
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Register this device to sign in securely using Face ID, Touch ID,
                        Windows Hello, or your device screen lock.
                      </Typography>

                      {!passkeySupported ? (
                        <Alert severity="warning" sx={{ mt: 1.5 }}>
                          Passkeys are not supported by this browser or device.
                        </Alert>
                      ) : null}

                      <TextField
                        size="small"
                        label="Passkey name (optional)"
                        placeholder="Example: Office MacBook"
                        fullWidth
                        value={passkeyName}
                        onChange={(e) => setPasskeyName(e.target.value)}
                        sx={{ ...inputSx, mt: 1.5 }}
                        inputProps={{ maxLength: 150 }}
                      />

                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!token || !passkeySupported || passkeyLoading}
                        onClick={handleRegisterPasskey}
                        sx={{ ...primaryBtnSx, mt: 1.5 }}
                        type="button"
                      >
                        {passkeyLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          "Register Passkey"
                        )}
                      </Button>

                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        UniFLOO stores only the public credential. Your biometric data never leaves your device.
                      </Typography>
                    </Box>
                  </Paper>

                  <Paper variant="outlined" sx={sectionSx}>
                    <Box sx={{ pl: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: ORANGE }}>
                        Two-Factor Authentication
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                        Add an authenticator app as an additional verification method.
                      </Typography>

                      <Button
                        variant="outlined"
                        fullWidth
                        disabled={!token || loading2FA}
                        onClick={start2FARegistration}
                        sx={outlinedBtnSx}
                        type="button"
                      >
                        {loading2FA ? <CircularProgress size={20} /> : "Set up Authenticator (2FA)"}
                      </Button>
                    </Box>
                  </Paper>
                </Stack>

                {/* ✅ Telegram + Announcements in themed section */}
                <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
                  <Box sx={{ pl: 1 }}>
                    {renderTelegramSection()}
                    {renderAnnouncementsSection()}
                  </Box>
                </Paper>
              </>
            ) : (
              <>
                <Typography sx={{ fontWeight: 900, color: ORANGE, mb: 1 }}>
                  Two-Factor Authentication
                </Typography>

                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    1. Install Google Authenticator / Microsoft Authenticator
                    <br />
                    2. Scan the QR code
                    <br />
                    3. Enter the 6-digit code below
                  </Typography>

                  {qrCode ? (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        component="img"
                        src={qrCode}
                        alt="Authenticator QR"
                        sx={{
                          width: 190,
                          height: 190,
                          borderRadius: 2,
                          border: `1px solid ${BORDER}`,
                          backgroundColor: "white",
                          p: 1,
                        }}
                      />
                    </Box>
                  ) : null}

                  <TextField
                    size="small"
                    label="6-digit code"
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    inputProps={{ inputMode: "numeric" }}
                    required
                    sx={inputSx}
                  />

                  <Button
                    variant="contained"
                    onClick={handleVerify2FA}
                    disabled={loading2FA}
                    sx={primaryBtnSx}
                    type="button"
                  >
                    {loading2FA ? <CircularProgress size={20} /> : "Verify & Enable 2FA"}
                  </Button>
                </Stack>

                <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
                  <Box sx={{ pl: 1 }}>
                    <Typography sx={{ fontWeight: 900, color: ORANGE }}>
                      Push Notifications
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Register push notifications from Security Settings in the installed UniFLOO mobile app.
                    </Typography>
                  </Box>
                </Paper>

                {/* ✅ Telegram + Announcements also available here */}
                <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
                  <Box sx={{ pl: 1 }}>
                    {renderTelegramSection()}
                    {renderAnnouncementsSection()}
                  </Box>
                </Paper>
              </>
            )}
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
};

export default ResetPassword;
